import { useState } from "react";
import { IconMinus, IconPlus, IconScale, IconCoin } from "@tabler/icons-react";
import "../Styles/CartWeightAdjuster.css";

const MIN_AMOUNT = 1000;
const AMOUNT_STEP = 500;
const AMOUNT_PRESETS = [1000, 1500, 2000, 3000];

const MIN_GRAMS = 100;
const GRAM_STEP = 10;

const formatPrice = (price) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Math.round(price),
  );

const parseGramsFromLabel = (label) => {
  const match = /(\d+)/.exec(label || "");
  return match ? Number(match[1]) : MIN_GRAMS;
};

/**
 * Contrôle compact utilisé DANS une ligne du panier (remplace le +/- de
 * quantité pour les produits vendus au poids). Chaque changement déclenche
 * immédiatement onChange({ price, weightLabel }) — pas de bouton "valider".
 */
const CartWeightAdjuster = ({ pricePerKg, currentWeightLabel, onChange }) => {
  const [mode, setMode] = useState("poids"); // "montant" | "poids"
  const [amount, setAmount] = useState(MIN_AMOUNT);
  const [grams, setGrams] = useState(() =>
    parseGramsFromLabel(currentWeightLabel),
  );

  const commit = (nextMode, nextAmount, nextGrams) => {
    const price =
      nextMode === "montant"
        ? nextAmount
        : Math.round((pricePerKg * nextGrams) / 1000);
    const estGrams =
      nextMode === "montant" && pricePerKg > 0
        ? Math.round((nextAmount / pricePerKg) * 1000)
        : nextGrams;

    onChange({ price, weightLabel: `≈ ${estGrams} g` });
  };

  const switchMode = (nextMode) => {
    setMode(nextMode);
    commit(nextMode, amount, grams);
  };

  const adjustGrams = (delta) => {
    setGrams((prev) => {
      const next = Math.max(MIN_GRAMS, prev + delta);
      commit("poids", amount, next);
      return next;
    });
  };

  const handlePreset = (preset) => {
    setAmount(preset);
    commit("montant", preset, grams);
  };

  const handleAmountChange = (e) => {
    const value = Number(e.target.value);
    setAmount(Number.isNaN(value) ? MIN_AMOUNT : value);
  };

  const handleAmountBlur = () => {
    setAmount((prev) => {
      const clamped = Math.max(MIN_AMOUNT, Math.round(prev / 100) * 100);
      commit("montant", clamped, grams);
      return clamped;
    });
  };

  return (
    <div className="cwa">
      <div className="cwa-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "poids"}
          className={`cwa-tab ${mode === "poids" ? "is-active" : ""}`}
          onClick={() => switchMode("poids")}
        >
          <IconScale size={13} />
          Poids
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "montant"}
          className={`cwa-tab ${mode === "montant" ? "is-active" : ""}`}
          onClick={() => switchMode("montant")}
        >
          <IconCoin size={13} />
          Montant
        </button>
      </div>

      {mode === "poids" ? (
        <div className="cwa-grams-stepper">
          <button
            type="button"
            onClick={() => adjustGrams(-GRAM_STEP)}
            disabled={grams <= MIN_GRAMS}
            aria-label={`Diminuer de ${GRAM_STEP}g`}
          >
            <IconMinus size={13} />
          </button>
          <span className="cwa-grams-value">{grams} g</span>
          <button
            type="button"
            onClick={() => adjustGrams(GRAM_STEP)}
            aria-label={`Augmenter de ${GRAM_STEP}g`}
          >
            <IconPlus size={13} />
          </button>
        </div>
      ) : (
        <div className="cwa-amount-block">
          <div className="cwa-presets">
            {AMOUNT_PRESETS.map((preset) => (
              <button
                type="button"
                key={preset}
                className={`cwa-preset ${amount === preset ? "is-active" : ""}`}
                onClick={() => handlePreset(preset)}
              >
                {formatPrice(preset)}F
              </button>
            ))}
          </div>
          <div className="cwa-amount-row">
            <input
              type="number"
              min={MIN_AMOUNT}
              step={AMOUNT_STEP}
              value={amount}
              onChange={handleAmountChange}
              onBlur={handleAmountBlur}
              aria-label="Montant en FCFA"
            />
            <span>F</span>
          </div>
        </div>
      )}

      <p className="cwa-hint">
        {mode === "poids"
          ? `Paliers de ${GRAM_STEP}g · min. ${MIN_GRAMS}g`
          : `Min. ${formatPrice(MIN_AMOUNT)} FCFA`}
      </p>
    </div>
  );
};

export default CartWeightAdjuster;
